import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  isCourtesyVehicleAlreadyRegisteredError,
  useCreateCourtesyVehicle,
  useUpdateCourtesyVehicle,
  type CourtesyVehicle,
} from '@/hooks/useCourtesyVehicles';
import { useCentsInput } from '@/hooks/useCentsInput';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';

interface CourtesyVehicleFormDialogProps {
  open: boolean;
  vehicle?: CourtesyVehicle | null;
  onClose: () => void;
}

export function CourtesyVehicleFormDialog({
  open,
  vehicle,
  onClose,
}: CourtesyVehicleFormDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!vehicle;

  const [registrationNumber, setRegistrationNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dailyRateCents, setDailyRateCents] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const rateInput = useCentsInput(dailyRateCents, setDailyRateCents, vehicle?.id ?? 'new');

  const createMutation = useCreateCourtesyVehicle();
  const updateMutation = useUpdateCourtesyVehicle();
  const isLoading = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) return;
    if (vehicle) {
      setRegistrationNumber(vehicle.registrationNumber);
      setDisplayName(vehicle.displayName ?? '');
      setDailyRateCents(vehicle.dailyRateCents ?? 0);
      setIsActive(vehicle.isActive);
    } else {
      setRegistrationNumber('');
      setDisplayName('');
      setDailyRateCents(0);
      setIsActive(true);
    }
    setIsDirty(false);
  }, [open, vehicle]);

  const handleSubmit = async () => {
    const plate = registrationNumber.trim();
    if (!isEditing && !plate) return;

    const name = displayName.trim();
    try {
      if (isEditing && vehicle) {
        const data: { displayName: string | null; dailyRateCents: number; isActive: boolean } = {
          displayName: name.length > 0 ? name : null,
          dailyRateCents,
          isActive,
        };
        await updateMutation.mutateAsync({ id: vehicle.id, data });
        notify.success(t('courtesy.updated'), t('courtesy.updatedDesc', { plate: vehicle.registrationNumber }));
      } else {
        const data: {
          registrationNumber: string;
          dailyRateCents: number;
          isActive: boolean;
          displayName?: string;
        } = {
          registrationNumber: plate,
          dailyRateCents,
          isActive,
        };
        if (name.length > 0) data.displayName = name;
        await createMutation.mutateAsync(data);
        notify.success(t('courtesy.created'), t('courtesy.createdDesc', { plate }));
      }
      onClose();
    } catch (error) {
      notify.error(
        tc('error'),
        isCourtesyVehicleAlreadyRegisteredError(error)
          ? t('courtesy.alreadyRegistered')
          : describeApiError(error, tc, { fallback: t('courtesy.saveFailed') }),
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('courtesy.editTitle') : t('courtesy.addTitle')}
      isDirty={isDirty}
      onSubmit={() => { void handleSubmit(); }}
      isLoading={isLoading}
      submitDisabled={!isEditing && registrationNumber.trim().length === 0}
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('courtesy.plate')}
            value={registrationNumber}
            onChange={(event) => {
              setRegistrationNumber(event.target.value);
              setIsDirty(true);
            }}
            required={!isEditing}
            disabled={isEditing}
            autoFocus={!isEditing}
            helperText={t('courtesy.plateHint')}
            slotProps={{ htmlInput: { maxLength: 20 } }}
          />
          <TextField
            label={t('courtesy.displayName')}
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
              setIsDirty(true);
            }}
            helperText={t('courtesy.displayNameHint')}
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
          <TextField
            label={t('courtesy.dailyRate')}
            value={rateInput.value}
            onChange={(event) => {
              rateInput.onChange(event as ChangeEvent<HTMLInputElement>);
              setIsDirty(true);
            }}
            onFocus={rateInput.onFocus}
            onBlur={rateInput.onBlur}
            helperText={t('courtesy.dailyRateHint')}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => {
                  setIsActive(event.target.checked);
                  setIsDirty(true);
                }}
                slotProps={{ input: { 'aria-label': t('courtesy.active') } }}
              />
            }
            label={t('courtesy.active')}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
